import React, { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/router';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DEFAULT_VENUE_IMAGE_LOGO } from 'constants/venues';
import { useGetVenueForEventByIdQuery } from 'types/generated/client';
import { getMaximumVairTeamsPerGame, getMinimumVairGames } from 'utils/shared/vair/scheduler';
import { useModal } from 'hooks/useModal';
import { useVairConnection } from 'hooks/useVairConnection';
import { Button } from 'components/Button';
import AddVenue from 'components/forms/AddVenue';
import Switch from 'components/forms/Switch';
import InputField from 'components/tournaments/InputField';
import { InputType } from 'components/tournaments/InputField/InputField';
import classNames from 'styles/utils/classNames';
import ExternalLink from 'svg/ExternalLink';
import ButtonToggler from './ButtonToggler';
import Counter from './Counter';
import WhenAndWhereTimeSlot from './WhenAndWhereTimeSlot';
import {
  DEFAULT_VAIR_SCORE_LIMIT,
  FormData,
  QuickCreateFormTypes,
  VAIR_SCORE_OPTIONS,
  WhenAndWhereData,
  whenAndWhereSchema,
} from '../types';

const ID_VENUE_SELECT = 'venue-select';

const DEFAULT_VAIR_EVENT_TITLE = 'VAIR Round Robin';

const WhenAndWhere: React.FC<QuickCreateFormTypes> = ({
  formData,
  setFormData,
  nextStep,
  prevStep,
  handleSubmitData,
  mode = 'create',
  onUpdate,
  eventId,
  initialData,
  className = '',
}) => {
  const router = useRouter();
  const { data: venueData } = useGetVenueForEventByIdQuery({
    variables: {
      id: router.query.venueId,
    },
    skip: !router?.query?.venueId,
  });
  const defaultSelectDate = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  })();

  // Use initialData for edit mode, formData for create mode
  const sourceData = mode === 'edit' && initialData ? initialData : formData?.whenAndWhere;

  const defaultValues: WhenAndWhereData = {
    selectDate: sourceData?.selectDate || defaultSelectDate,
    selectStartTime: sourceData?.selectStartTime || 0,
    venue: sourceData?.venue || {
      address: '',
      id: '',
      latitude: 0,
      longitude: 0,
      venueImageURL: '',
      city: null,
      timezone: null,
    },
    minCourts: sourceData?.minCourts || 0,
    title: sourceData?.title || (sourceData?.isVairEvent ? DEFAULT_VAIR_EVENT_TITLE : ''),
    isVairEvent: sourceData?.isVairEvent || false,
    vairFormat: sourceData?.vairFormat,
    isVairRequired: sourceData?.isVairRequired || false,
    numberOfPlayers: sourceData?.numberOfPlayers || 8,
    scoreLimit: sourceData?.scoreLimit ?? DEFAULT_VAIR_SCORE_LIMIT,
  };

  const {
    control,
    handleSubmit,
    setValue,
    register,
    getValues,
    formState: { errors, isValid },
    watch,
  } = useForm<WhenAndWhereData>({
    resolver: zodResolver(whenAndWhereSchema),
    defaultValues,
  });

  const isVairEvent = watch('isVairEvent');
  const isVairRequired = watch('isVairRequired');
  const vairFormat = watch('vairFormat');
  const formVenue = getValues('venue');
  const {
    isConnected,
    openVairConnectionModal,
    VairConnectionModal,
    VairConnectionStatusModal,
  } = useVairConnection();

  // The "VAIR required" toggle only applies to the Traditional Round Robin format.
  const showVairRequiredToggle = vairFormat === 'traditional-rr';
  // A connected VAIR account is needed for a VAIR event OR a VAIR-required event.
  const requiresVairConnection = isVairEvent || !!isVairRequired;
  const isMissingVairConnection = requiresVairConnection && !isConnected;

  // Prompt to connect VAIR the moment it's required (mirrors the SelectVairFormat gate).
  const handleToggleVairRequired = () => {
    const next = !getValues('isVairRequired');
    setValue('isVairRequired', next, { shouldValidate: true });
    if (next && !isConnected) {
      openVairConnectionModal('create');
    }
  };

  const onSubmit: SubmitHandler<WhenAndWhereData> = async (data) => {
    console.log('data in whenandwhere submit handler', data);

    // Prevent submission if a VAIR connection is required (VAIR event or VAIR-required) but not synced
    if ((data.isVairEvent || !!data.isVairRequired) && !isConnected) {
      return;
    }

    // Handle edit mode
    if (mode === 'edit' && onUpdate) {
      await onUpdate(data);
      return;
    }

    // Handle create mode (existing logic)
    if (setFormData) {
      setFormData((prevData) => ({ ...prevData, whenAndWhere: data }));

      // For VAIR events, skip Events and ParticipantsAndGames steps and submit directly
      if (data.isVairEvent && handleSubmitData) {
        // Create minimal formData for VAIR events (no events/participants needed)
        const vairFormData: FormData = {
          eventId: formData?.eventId || '',
          whenAndWhere: data,
          events: {
            maxEvents: 1,
            events: [
              {
                doubles: 'VAIR Event',
                singles: '',
                activeTab: 0,
                eventId: 1,
              },
            ],
          },
          participantsAndGames: [
            {
              eventId: 1,
              maxTeams: getMaximumVairTeamsPerGame(),
              minGames: getMinimumVairGames(),
              eventTitle: 'VAIR Event',
              eventTab: 'VAIR',
            },
          ],
        };
        handleSubmitData(vairFormData);
      } else if (nextStep && formData?.whenAndWhere) {
        // For non-VAIR events, proceed to next step
        nextStep();
      }
    }
  };

  const {
    openModal: openVenueModal,
    isOpen: isVenueModalOpen,
    closeModal: closeVenueModal,
  } = useModal();

  useEffect(() => {
    if (errors.venue?.address) {
      document.getElementById(ID_VENUE_SELECT)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [errors.venue?.address]);

  useEffect(() => {
    if (venueData?.venuesByPk?.id && !formVenue?.id) {
      setValue('venue', {
        address: venueData?.venuesByPk?.addressString || '',
        id: venueData?.venuesByPk?.id || '',
        venueImageURL: venueData?.venuesByPk?.images?.[0]?.url || DEFAULT_VENUE_IMAGE_LOGO,
        latitude: venueData?.venuesByPk?.geometry?.coordinates?.[1] || 0,
        longitude: venueData?.venuesByPk?.geometry?.coordinates?.[0] || 0,
        city: venueData?.venuesByPk?.city,
        timezone: venueData?.venuesByPk?.timezone || null,
      });
    }
  }, [venueData?.venuesByPk?.id, formVenue?.id]);

  return (
    <>
      <div className={classNames('mx-auto flex w-full max-w-[23.5rem] flex-1 flex-col', className)}>
        <h5 className="typography-product-heading mb-6 text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {mode === 'edit' ? 'Edit Event Details' : 'The Details'}
        </h5>

        <form onSubmit={handleSubmit(onSubmit)} className="flex h-full flex-1 flex-col gap-6 pb-10">
          <div>
            <Controller
              name={`title`}
              control={control}
              render={({ field }) => (
                <InputField
                  fieldLabel="Title"
                  errors={errors}
                  placeholder="Title"
                  name={`title`}
                  register={register}
                  inputType={InputType.LabelInput}
                />
              )}
            />
          </div>

          {isVairEvent && (
            <div>
              <label className="typography-product-subheading mb-2 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                Score limit
              </label>
              <Controller
                name="scoreLimit"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="flex gap-4">
                    {VAIR_SCORE_OPTIONS.map((limit) => (
                      <button
                        key={limit}
                        type="button"
                        className={classNames(
                          'typography-product-element-label w-full rounded-md px-4 py-2',
                          (value ?? 11) === limit
                            ? 'bg-black text-white dark:bg-white dark:text-black'
                            : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
                        )}
                        onClick={() => onChange(limit)}
                      >
                        {limit}
                      </button>
                    ))}
                  </div>
                )}
              />
            </div>
          )}

          {/* Select Date and Time */}
          <WhenAndWhereTimeSlot
            selectStartTime="selectStartTime"
            control={control}
            errors={errors}
          />

          {showVairRequiredToggle && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <img src="/images/vair/vair-black.png" alt="VAIR" className="h-4 w-auto dark:invert" />
                  <span className="typography-product-element-label font-bold text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    Required
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="typography-product-element-label font-bold text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {isVairRequired ? 'YES' : 'NO'}
                  </span>
                  <Switch
                    isActive={!!isVairRequired}
                    toggleIsActive={handleToggleVairRequired}
                    ariaDescription="Require VAIR rating for this event"
                    className="!ml-0 w-auto !max-w-none"
                  />
                </div>
              </div>
              <a
                href="https://www.vairified.com"
                target="_blank"
                rel="noopener noreferrer"
                className="typography-product-element-label inline-flex w-fit items-center gap-1 text-color-text-brand"
              >
                What is VAIR
                <ExternalLink className="size-4" />
              </a>
            </div>
          )}

          {/* Location */}
          <div>
            <label
              htmlFor="selectTime"
              className="typography-product-subheading mb-3 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
            >
              Location
            </label>
            <Controller
              control={control}
              name="venue"
              render={({ field: { onChange } }) => (
                <AddVenue
                  title="Add location"
                  isOpen={isVenueModalOpen}
                  onClose={closeVenueModal}
                  onSubmit={(venue) => {
                    onChange({
                      address: venue?.addressString || '',
                      id: venue?.id || '',
                      venueImageURL: venue?.images?.[0]?.url || '',
                      latitude: venue?.latitude || 0,
                      longitude: venue?.longitude || 0,
                      city: venue.city,
                      timezone: venue?.timezone || null,
                    });
                    closeVenueModal();
                  }}
                  enableManualAddress={true}
                  onSubmitManualAddress={(val) => {
                    onChange({
                      address: val?.address || '',
                      id: '',
                      latitude: val?.latitude || 0,
                      longitude: val?.longitude || 0,
                      venueImageURL: '',
                      city: null,
                      timezone: null,
                    });
                    closeVenueModal();
                  }}
                />
              )}
            />

            {getValues('venue')?.address ? (
              <div id={ID_VENUE_SELECT} className="space-y-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex gap-3">
                    <img
                      src={getValues('venue').venueImageURL || DEFAULT_VENUE_IMAGE_LOGO}
                      alt={getValues('venue')?.address || ''}
                      className="h-16 max-w-[6.3rem] rounded object-cover"
                    />
                    <span className="typography-product-subheading line-clamp-3 h-full text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      {getValues('venue').address}
                    </span>
                  </div>

                  <Button
                    variant="inverted"
                    className="h-10 w-full md:w-40"
                    size="md"
                    onClick={() => openVenueModal()}
                  >
                    Change location
                  </Button>
                </div>

                {errors.venue?.address && (
                  <p className="text-sm text-color-text-lightmode-error dark:text-color-text-darkmode-error">
                    You must be hosting at a valid venue. If your venue isn't listed, email us at
                    team@bounce.game
                  </p>
                )}

                {getValues('venue')?.id === '' && !isVairEvent && (
                  <Counter
                    label="How many courts will you use?"
                    errors={errors}
                    inputProps={{
                      type: 'number',
                      onWheel: (e) => {
                        e.preventDefault();
                        e.currentTarget.blur();
                      },
                      min: 0,
                    }}
                    name="minCourts"
                    register={register}
                    setValue={setValue}
                    getValues={getValues}
                    control={control}
                  />
                )}
              </div>
            ) : (
              <div id={ID_VENUE_SELECT} className="space-y-2">
                {errors.venue?.address?.message && (
                  <p className="text-sm text-color-text-lightmode-error dark:text-color-text-darkmode-error">
                    {errors.venue?.address?.message}
                  </p>
                )}
                <Button
                  variant="primary"
                  className="w-full"
                  size="lg"
                  onClick={() => openVenueModal()}
                >
                  Add location
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>
      <ButtonToggler
        type="button"
        isLoading={false}
        prevStep={prevStep}
        nextStep={() => {
          if (isMissingVairConnection) {
            return;
          }
          if (!isValid) {
            handleSubmit(onSubmit)();
          } else if (formData?.whenAndWhere || mode === 'edit') {
            handleSubmit(onSubmit)();
          }
        }}
        nextTitle={mode === 'edit' ? 'Update' : isVairEvent ? 'Create' : 'Next'}
        disabled={isMissingVairConnection}
        buttonClassName={mode === 'edit' ? '!w-full' : ''}
      />

      {/* Vair connection prompt + status modals (shown when rating is required) */}
      {VairConnectionModal}
      {VairConnectionStatusModal}
    </>
  );
};

export default WhenAndWhere;
